import Table from 'react-bootstrap/Table';
import React, { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import { IoMdAdd } from "react-icons/io";
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2'; // Beautiful alerts instead of default alert/confirm

function Contact() {
  // Get accountId from URL params
  const { accountId } = useParams();
  const navigate = useNavigate();

  // State for contacts list, error message, and loading status
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch all contacts for this account when component mounts or accountId changes
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/contacts/${accountId}`);

        // Update contacts state with data from backend
        setContacts(response.data.data || []);
      } catch (error) {
        // Handle fetch error – show user-friendly message
        setError(error.response?.data?.message || 'Failed to fetch contacts. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (accountId) {
      fetchContacts();
    } else {
      setError('Invalid account ID');
      setLoading(false);
    }
  }, [accountId]);

  // Delete a contact – uses SweetAlert2 for confirmation and feedback
  const handleDeleteContact = async (contactId) => {
    // Beautiful confirmation dialog
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You want to delete this contact? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        // Send DELETE request to backend
        await axios.delete(`https://hms.backend.hiragroupcloud.com/api/contacts/${contactId}`);

        // Remove contact from local state (optimistic update)
        setContacts(contacts.filter(contact => contact._id !== contactId));

        // Success message with SweetAlert2
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Contact has been deleted successfully.',
          confirmButtonColor: '#004aad',
          timer: 3000,
          timerProgressBar: true
        });
      } catch (error) {
        // Error message if delete fails
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete contact. Please try again.',
          confirmButtonColor: '#d33'
        });
      }
    }
  };

  // Table styling – modern glassmorphism look
  const tableStyle = {
    fontSize: '0.95rem',
    width: '100%',
    margin: '30px 0',
    borderCollapse: 'separate',
    borderSpacing: '0',
    borderRadius: '10px',
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
    fontFamily: "'Poppins', 'Segoe UI', sans-serif",
    transition: 'all 0.3s ease-in-out',
  };

  // Cell styling for clean appearance
  const cellStyle = {
    padding: '12px 18px',
    textAlign: 'left',
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    color: '#2c3e50',
    backgroundColor: 'transparent',
    fontSize: '0.95rem',
    lineHeight: '1.5',
    letterSpacing: '0.25px',
    borderRight: 'none',
    borderLeft: 'none',
  };

  // Placeholder save action (can be extended later)
  const handleSaveClick = () => {
    Swal.fire({
      icon: 'success',
      title: 'Saved!',
      text: 'Provider details saved successfully!',
      confirmButtonColor: '#004aad',
      timer: 2500,
      timerProgressBar: true
    });
  };

  // Loading state
  if (loading) {
    return <div>Loading contacts...</div>;
  }

  return (
    <div>
      {/* Error message display */}
      {error && <div style={{ color: 'red', marginBottom: '10px', fontWeight: 'bold' }}>{error}</div>}

      {/* Add New Contact Button */}
      <Link to={`/account/${accountId}/contacts/add`} style={{ textDecoration: 'none' }}>
        <Button
          style={{
            backgroundColor: '#004aad',
            borderColor: '#004aad',
            marginBottom: '20px',
            marginLeft: 'auto',
            display: 'block',
          }}
        >
          <IoMdAdd style={{ marginRight: '5px' }} /> Add
        </Button>
      </Link>

      {/* Contacts Table */}
      <Table bordered hover style={tableStyle}>
        <thead style={{ backgroundColor: '#f0f0f0' }}>
          <tr>
            <th style={cellStyle}>Primary</th>
            <th style={cellStyle}>Name</th>
            <th style={cellStyle}>Email</th>
            <th style={cellStyle}>Role</th>
            <th style={cellStyle}>E-Billing</th>
            <th style={cellStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {contacts.length > 0 ? (
            contacts.map((contact, index) => (
              <tr key={contact._id || index}>
                {/* Primary Contact Checkbox */}
                <td style={cellStyle}>
                  <input type="checkbox" defaultChecked={contact.isPrimary} readOnly />
                </td>

                {/* Full Name */}
                <td style={cellStyle}>
                  {`${contact.title ? contact.title + ' ' : ''}${contact.firstName} ${contact.middleInitial ? contact.middleInitial + ' ' : ''}${contact.lastName}`}
                </td>

                {/* Email with mailto link */}
                <td style={cellStyle}>
                  {contact.email && (
                    <a
                      href={`https://mail.google.com/mail/?view=cm&fs=1&to=${contact.email}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#004aad', textDecoration: 'none' }}
                    >
                      {contact.email}
                    </a>
                  )}
                </td>

                {/* Role */}
                <td style={cellStyle}>{contact.role || '-'}</td>

                {/* E-Billing Status */}
                <td style={cellStyle}>
                  <input type="checkbox" checked={contact.eBilling || false} readOnly />
                </td>

                {/* Action Buttons: Edit & Delete */}
                <td style={cellStyle}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {/* Edit Button */}
                    <Link to={`/account/${accountId}/contacts/edit/${contact._id}`} style={{ textDecoration: "none" }}>
                      <Button
                        variant="success"
                        size="sm"
                        style={{
                          backgroundColor: "#004aad",
                          borderColor: "#004aad",
                          color: "#fff",
                          fontWeight: "500",
                          padding: "6px 12px",
                          borderRadius: "12px",
                          fontSize: "0.88rem",
                          letterSpacing: "0.3px",
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                          transition: "all 0.3s ease-in-out",
                          textTransform: "uppercase",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#0060bb";
                          e.target.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "#004aad";
                          e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
                        }}
                      >
                        Edit
                      </Button>
                    </Link>

                    {/* Delete Button with SweetAlert confirmation */}
                    <Button
                      variant="danger"
                      size="sm"
                      style={{
                        backgroundColor: "#dc3545",
                        borderColor: "#dc3545",
                        color: "#fff",
                        fontWeight: "500",
                        padding: "6px 12px",
                        borderRadius: "12px",
                        fontSize: "0.88rem",
                        letterSpacing: "0.3px",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                        transition: "all 0.3s ease-in-out",
                        textTransform: "uppercase",
                      }}
                      onClick={() => handleDeleteContact(contact._id)}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            // No contacts message
            <tr>
              <td colSpan="6" style={{ ...cellStyle, textAlign: 'center', fontStyle: 'italic' }}>
                No contacts available.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Save Button (bottom) – shows success alert */}
      <Button
        style={{
          backgroundColor: '#004aad',
          borderColor: '#004aad',
          marginLeft: '20px',
          padding: '8px 16px',
          display: 'inline-flex',
          alignItems: 'center',
        }}
        onClick={handleSaveClick}
      >
        Save
      </Button>
    </div>
  );
}

export default Contact;